import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { EnvironmentService } from './services/environment.service';
import { ModalComponent } from './components/modal/modal.component';
import { NotificationComponent } from './components/notification/notification.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ModalComponent, NotificationComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  title = 'Docsila';
  private readonly _destroying$ = new Subject<void>();
  isIframe = false;
  private redirectHandled = false;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private environmentService: EnvironmentService
  ) {
    this.environmentService.logEnvironmentInfo();
  }

  ngOnInit(): void {
    // Check if running in iframe (for silent token refresh)
    this.isIframe = window !== window.parent && !window.opener;
    
    if (this.isIframe) {
      return;
    }

    // Handle MSAL redirect on app initialization - this is critical!
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result) {
          // Clear cached role on fresh login to force role check
          sessionStorage.removeItem('userRole');
          sessionStorage.removeItem('userEmail');
          sessionStorage.removeItem('userSessionId');
          this.setActiveAccount();
          this.redirectHandled = true;
          // Only navigate to new first dashboard if on root or login page
          // Use window.location.pathname because router.url may not be initialized yet
          const currentPath = window.location.pathname;
          if (
            currentPath === '/' ||
            currentPath === '/login' ||
            currentPath.startsWith('/login') ||
            currentPath === '/docs/oauth2-redirect' ||
            currentPath.startsWith('/docs/oauth2-redirect')
          ) {
            this.router.navigate(['/home']);
          } else {
          }
        } else {
          // Check if already authenticated
          this.checkAndLogAuthStatus();
        }
      },
      error: (error) => {
      }
    });

    // Listen for login success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        // Clear cached role on login success
        sessionStorage.removeItem('userRole');
        sessionStorage.removeItem('userEmail');
        sessionStorage.removeItem('userSessionId');
        this.setActiveAccount();
        
        // Navigate if not already handled and on root/login page
        if (!this.redirectHandled) {
          const currentPath = window.location.pathname;
          if (
            currentPath === '/' ||
            currentPath === '/login' ||
            currentPath.startsWith('/login') ||
            currentPath === '/docs/oauth2-redirect' ||
            currentPath.startsWith('/docs/oauth2-redirect')
          ) {
            this.router.navigate(['/home']);
          }
        }
      });

    // Set active account when interaction completes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setActiveAccount();
        this.checkAndLogAuthStatus();
      });
  }

  private setActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const allAccounts = this.msalService.instance.getAllAccounts();
    if (!activeAccount && allAccounts.length > 0) {
      this.msalService.instance.setActiveAccount(allAccounts[0]);
    }
  }

  private checkAndLogAuthStatus(): void {
    const accounts = this.msalService.instance.getAllAccounts();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}

